import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getAttribution } from '@/lib/attribution';
import { roleFamilyFor } from '@/lib/offers';
import { siteEvents } from '@/lib/siteEvents';

/**
 * After-apply newsletter opt-in (tracker LEAD-04).
 *
 * About half of all job-board captures are applicants to non-remote roles, and
 * they joined no newsletter: capture-job-applicant subscribes only remote
 * applicants (to The Remote Accountant / The Finance Nomad). This offers ONE
 * newsletter that fits the role, as an explicit button on the success screen.
 * Nothing is pre-ticked and nothing happens without the click:
 *
 *   close roles (roleFamilyFor 'close')  -> Month-End Closers (mec)
 *   finance / FP&A ('finance')           -> Modern Accounting (ma)
 *   anything else                        -> nothing. Remote applicants to those
 *                                           roles are already on The Remote
 *                                           Accountant from the apply itself, so
 *                                           a second ask would only send a
 *                                           confirmation for a list they are on.
 *
 * The click writes a waitlist_interest row (intent 'newsletter', the anon
 * insert policy), which runs the standing double opt-in: waitlist-confirm
 * emails one confirmation link (copy for context.surface 'job_apply'), and
 * newsletter-signup-sync subscribes the address within 10 minutes of the click
 * on it, with utm_medium job_apply and the tags below. The browser's
 * first/last-touch attribution rides in context.attribution, as on every other
 * waitlist capture. The apply form and its capture are untouched.
 */

type ListKey = 'mec' | 'ma';

const LISTS: Record<ListKey, { name: string; line: string }> = {
  mec: {
    name: 'Month-End Closers',
    line: 'Everything that moved in accounting this week, in five minutes. Free, unsubscribe in one click.',
  },
  ma: {
    name: 'Modern Accounting',
    line: 'AI workflows finance teams can run the same afternoon. Free, unsubscribe in one click.',
  },
};

const DONE_KEY = (list: ListKey) => `af_nl_optin:${list}`;

function newsletterForRole(title: string | undefined): ListKey | null {
  const family = roleFamilyFor(title);
  if (family === 'close') return 'mec';
  if (family === 'finance') return 'ma';
  return null;
}

function alreadyAsked(list: ListKey): boolean {
  try {
    return localStorage.getItem(DONE_KEY(list)) === '1';
  } catch {
    return false;
  }
}

export function ApplyNewsletterOptIn({
  email,
  jobTitle,
  jobId,
  isRemote,
}: {
  email: string;
  jobTitle: string;
  jobId?: string;
  isRemote?: boolean;
}) {
  const list = newsletterForRole(jobTitle);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [hidden] = useState(() => (list ? alreadyAsked(list) : true));

  useEffect(() => {
    if (list && !hidden) siteEvents.act('apply_newsletter_view', { list });
  }, [list, hidden]);

  if (!list || hidden || !email || !email.includes('@')) return null;
  const { name, line } = LISTS[list];

  const optIn = async () => {
    if (state === 'sending' || state === 'sent') return;
    setState('sending');
    siteEvents.act('apply_newsletter_click', { list });
    const family = roleFamilyFor(jobTitle);
    const attribution = getAttribution();
    try {
      const { error } = await (supabase.from as unknown as (t: string) => {
        insert: (v: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>;
      })('waitlist_interest').insert({
        email: email.trim().toLowerCase(),
        source: 'job_apply',
        intent: 'newsletter',
        context: {
          lists: [list],
          surface: 'job_apply',
          medium: 'job_apply',
          campaign: `apply_${family}`,
          tags: ['job-apply', `role-${family}`],
          job_id: jobId ?? null,
          job_title: jobTitle.slice(0, 200),
          is_remote: !!isRemote,
          ...(attribution ? { attribution } : {}),
        },
      });
      if (error) throw new Error(error.message);
      try { localStorage.setItem(DONE_KEY(list), '1'); } catch { /* storage blocked */ }
      setState('sent');
    } catch (e) {
      console.error('apply newsletter opt-in failed', e);
      setState('error');
    }
  };

  return (
    <div className="af-offer af-offer--compact" data-newsletter={list} style={{ marginTop: 14 }}>
      <div className="af-offer__text">
        <span className="af-offer__kicker">Free newsletter</span>
        <h3 className="af-offer__headline">Want {name} too?</h3>
        <p className="af-offer__body" aria-live="polite">
          {state === 'sent'
            ? `Check your inbox: one click in the email we just sent starts ${name}. Nothing arrives until you click it.`
            : state === 'error'
              ? 'That did not go through. Try once more?'
              : line}
        </p>
      </div>
      {state !== 'sent' && (
        <button
          type="button"
          className="af-offer__btn"
          onClick={optIn}
          disabled={state === 'sending'}
          style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', justifyContent: 'center' }}
        >
          {state === 'sending' ? 'Sending...' : `Yes, send me ${name}`}
        </button>
      )}
    </div>
  );
}
